<template>
  <div class="tw:pl-4 tw:lg:pl-12 main-report-container">
    <div v-if="isLoading">
      <Spinner />
    </div>

    <div v-else>
      <div
        v-if="currentJob && currentJob.data && currentJob.isJobFound && isValidType"
        class="report-page"
        :class="{ sidebar: isSidebarVisible }"
      >
        <div id="main-vulnerability" class="main">
          <router-link class="contact-us-btn" :to="{ path: '/contact-us' }">
            Contact us for full report
          </router-link>

          <ReportHeader
            :should-hide-download-report-button="true"
            :is-report-loading-now="IsReportLoadingNow"
            :change-downloading-report-status="changeDownloadingReportStatus"
            :job="currentJob.data"
            :count="currentJob.data.statistics.percentageBySeverity.commonAmountVulnerabilities"
            :platform="currentJob.data.platform"
            :is-trial-integration-restriction="false"
            :is-false-positive="folderName === 'False Positives'"
            :all-folders-without-selected="allFoldersWithoutSelected"
            :selected-folder="selectedFolder"
            :folder-name="folderName"
            :integration-id="null"
            :is-job-owner="currentJob.data.isOwner"
            :job-type="currentJob.data.type"
            :all-vulnerabilities-count="allVulnerabilitiesCount"
            @toggleFolderSettingsPopUp="toggleFolderSettingsPopUp"
            @loadSpinner="loadSpinner"
          />
          <NewReportStatistic
            v-if="currentJob.data.statistics"
            :all-vulnerabilities-folder="allVulnerabilitiesFolder"
            :all-vulnerabilities-count="
              currentJob.data.statistics.percentageBySeverity.commonAmountVulnerabilities
            "
            :job="currentJob.data"
            :integration-id="null"
            :is-trial-integration-restriction="false"
            @loadSpinner="loadSpinner"
          />
        </div>
        <CategoriesSideBar
          :all-vulnerabilities-folder="allVulnerabilitiesFolder"
          :job="currentJob.data"
          :vulnerability-categories-list="currentJob.data.statistics.percentageByType"
          :refresh-loading="refreshLoading"
          :integration-id="null"
          :is-trial-restriction="false"
          :width="width"
          :branch-name="branchName"
          :vulnerability-category-id="vulnerabilityCategoryId"
          :is-mouse-leave="isMouseLeave"
          :category-id-to-highlight="categoryIdToHighlight"
        />
        <VulnerabilityInCodeGuest
          v-if="
            currentJob.data.statistics.percentageBySeverity.commonAmountVulnerabilities > 0 &&
            !allSeveritiesUnchecked
          "
          ref="vulnerabilityInCodeGuest"
          :is-trial-restriction="false"
          :vulnerabilities="vulnerabilityPage.vulnerabilities"
          :is-member="false"
          :is-owner="currentJob.data.isOwner"
          :version-id="currentId"
          :count="vulnerabilityPage.count"
          :allow-false-positive="true"
          :current-id="currentId"
          :page="vulnerabilityPage.page"
          :selected-category="selectedCategory"
          :refresh-loading="refreshLoading"
          :integration-id="null"
          :job-id="currentJob.data.id"
          :is-router-query-used="isRouterQueryUsed"
          @mouse-enter="handleMouseEnter"
          @is-mouse-leave="handleMouseLeave"
        />
        <a v-if="toggleDisplayBtnToTop" class="btn-to-top" @click="toTop">
          <svg
            width="56"
            height="56"
            viewBox="0 0 56 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="56" height="56" rx="28" fill="#F5F7FA" />
            <path
              d="M28 25.4142L20.7071 32.7071C20.3166 33.0976 19.6834 33.0976 19.2929 32.7071C18.9024 32.3166 18.9024 31.6834 19.2929 31.2929L27.2929 23.2929C27.6834 22.9024 28.3166 22.9024 28.7071 23.2929L36.7071 31.2929C37.0976 31.6834 37.0976 32.3166 36.7071 32.7071C36.3166 33.0976 35.6834 33.0976 35.2929 32.7071L28 25.4142Z"
              fill="#949DB8"
            />
          </svg>
        </a>
        <NoVulnerabilitiesBlock
          v-if="
            (!currentJob.data.statistics.percentageBySeverity.commonAmountVulnerabilities &&
              currentJob.isJobFound) ||
            allSeveritiesUnchecked
          "
          :integration-id="null"
          :job-id="currentJob.data.id"
          :version-id="currentId"
          :branch-name="branchName"
        />
        <div v-if="isVulnerabilityPageLoading" class="spinner__vp-wrapper">
          <div class="spinner__wp">
            <Spinner />
          </div>
        </div>
      </div>
      <PageNotFound v-else />
    </div>
  </div>
</template>

<script>
// Functions and constants
import { mapGetters } from 'vuex';
import { reportTime } from '../../../utils/time';
import { FolderNameEnum } from '../../../utils/constants/common';
import { adjustWidths, calculateTotalStatisticsCount, isEmpty } from '../../../services/functions';
import {
  successNotification,
  failNotification,
} from '@/services/notificationService/notificationService';
// Components
import ReportHeader from '../../../components/report/ReportHeader';
import NewReportStatistic from '../../../components/report/NewReportStatistic';
import CategoriesSideBar from '../../../components/report/CategoriesSideBar.vue';
import VulnerabilityInCodeGuest from '../../../components/scans/VulnerabilityInCodeGuest.vue';
import NoVulnerabilitiesBlock from '../../../components/report/components/NoVulnerabilitiesBlock';

// Common
import Spinner from '../../../components/Spinner';
import PageNotFound from '../../../components/pageNotFound/PageNotFound';

export default {
  name: 'GuestScanInfo',
  components: {
    ReportHeader,
    NewReportStatistic,
    CategoriesSideBar,
    VulnerabilityInCodeGuest,
    NoVulnerabilitiesBlock,
    Spinner,
    PageNotFound,
  },
  data() {
    return {
      debouncedWidth: 0,
      currentId: null,
      toggleDisplayBtnToTop: false,
      IsReportLoadingNow: false,
      count: 0,
      isLoading: false,
      refreshLoading: false,
      isVulnerabilityPageLoading: false,
      branchName: null,
      isValidType: true,
      isFolderSettingsPopUp: false,
      isRouterQueryUsed: false,
      folderName: '',
      foldersForDeletingNotEmpty: [],
      foldersForDeleting: {},
      vId: null,
      vulnerabilityCategoryId: null,
      isMouseLeave: false,
      processingFolder: null,
      processingFolderIndex: null,
      folderDeletionAccepted: false,
      editedFolderName: '',
    };
  },
  computed: {
    ...mapGetters({
      folders: 'jobs/folders',
      currentJob: 'jobs/currentJob',
      isSidebarVisible: 'filters/isSidebarVisible',
      selectedCategory: 'jobs/selectedCategory',
      vulnerabilityPage: 'jobs/vulnerabilityPage',
      allSeveritiesUnchecked: 'filters/allSeveritiesUnchecked',
      filters: 'filters/filters',
    }),
    categoryIdToHighlight() {
      if (
        !this.$route.params.vulnerabilityId ||
        !this.vulnerabilityPage ||
        !this.vulnerabilityPage.categoryIdToHighlight
      ) {
        return null;
      }

      return this.vulnerabilityPage.categoryIdToHighlight;
    },
    allFoldersWithoutSelected() {
      let folderName = this.folderName ? this.folderName : FolderNameEnum.ALL_VULNERABILITIES;

      const foldersToSort = this.folders || [];

      return foldersToSort.filter((folder) => folder && folder.name !== folderName);
    },
    selectedFolder() {
      let folderName = this.folderName ? this.folderName : FolderNameEnum.ALL_VULNERABILITIES;
      return (this.folders || []).filter((folder) => folder && folder.name === folderName);
    },
    folderList() {
      return (this.folders || []).filter(
        (folder) =>
          folder &&
          folder.name !== FolderNameEnum.FALSE_POSITIVES &&
          folder.name !== FolderNameEnum.ALL_VULNERABILITIES
      );
    },
    allVulnerabilitiesFolder() {
      return (this.folders || []).filter(
        (folder) => folder && folder.name === FolderNameEnum.ALL_VULNERABILITIES
      );
    },
    falsePositivesFolder() {
      return (this.folders || []).filter(
        (folder) => folder && folder.name === FolderNameEnum.FALSE_POSITIVES
      );
    },
    allVulnerabilitiesCount() {
      return this.calculateTotalStatisticsCount(this.allVulnerabilitiesFolder[0] || {});
    },
    customFolders() {
      return (this.folders || []).filter(
        (folder) =>
          folder &&
          folder.name !== FolderNameEnum.FALSE_POSITIVES &&
          folder.name !== FolderNameEnum.MAIN_REPORT &&
          folder.name !== FolderNameEnum.ALL_VULNERABILITIES
      );
    },
    width: {
      get() {
        return this.debouncedWidth;
      },
      async set(val) {
        await this.$nextTick();
        this.debouncedWidth = val;
      },
    },
  },
  watch: {
    '$route.query': {
      deep: true,
      async handler() {
        if (this.$route.query?.categories || this.$route.query?.page) {
          return;
        }

        this.isVulnerabilityPageLoading = true;
        this.isRouterQueryUsed = true;
        this.folderName = this.$route.query?.folderName || FolderNameEnum.ALL_VULNERABILITIES;
        this.$store.commit('jobs/deleteFilterVulnerability', {
          id: this.$route.params.id,
        });
        await this.loadPageInfo();

        this.scrollToVulnerability();

        // sidebar only on desktop and if filters is not empty
        this.width >= 900 && !isEmpty(this.filters)
          ? this.onSidebarShown()
          : this.onSidebarHidden();
        this.$store.commit('filters/updateAllSeveritiesUnchecked');

        this.isRouterQueryUsed = false;

        this.isVulnerabilityPageLoading = false;
        // Call adjustWidths after everything has been rendered
        this.adjustWidthsAfterRender();
        // set prevent page load duplication to false. It's needed when we move last vulnerability to another folder
        this.$store.commit('jobs/setPreventPageLoadDuplication', false);
      },
    },
    selectedCategory() {
      if (this.selectedCategory) {
        this.toVulnerabilityScroll('SELECTED_SCROLL', this.selectedCategory);
      }
    },
    // watch the innerWidth change and adjust
    width() {
      this.adjustWidthsAfterRender();
    },
  },
  async beforeMount() {
    this.isLoading = true;
    this.currentId = this.$route.params.id;
    this.branchName = this.$route.params.branchName;
    this.folderName = this.$route.query.folderName;
    this.vId = this.$route.query.vId;

    this.$store.commit('jobs/setCurrentJob', null);
    await this.loadPageInfo();

    if (this.currentJob && this.currentJob.data) {
      document.title = `${this.currentJob.data.fileName} — Report | Oversecured`;
    } else {
      document.title = 'Page not found | Oversecured';
    }

    this.isLoading = false;

    if (window.innerWidth) {
      this.width = window.innerWidth;
    }

    if (
      // eslint-disable-next-line no-prototype-builtins
      this.$route.query.hasOwnProperty('categories') &&
      this.$route.query.categories === ''
    ) {
      this.$store.commit('filters/updateAllSeveritiesUnchecked');
    }
  },
  mounted() {
    window.innerWidth >= 900 && !isEmpty(this.filters)
      ? this.onSidebarShown()
      : this.onSidebarHidden();
  },
  beforeRouteLeave(to, from, next) {
    this.onSidebarHidden();
    this.$store.commit('jobs/deleteFilterVulnerability', {
      id: this.$route.params.id,
    }); // clear filters vulnerabilityIds after leave page
    this.$store.dispatch('filters/clearFilters'); // clear filters after leave page
    next();
  },
  created() {
    window.addEventListener('scroll', this.handleScroll);
    // add event listener for resize event
    window.addEventListener('resize', this.resizeHandler);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.resizeHandler);
  },
  methods: {
    handleMouseEnter(id) {
      this.vulnerabilityCategoryId = id;
      this.isMouseLeave = false;
    },
    handleMouseLeave() {
      this.isMouseLeave = true;
    },
    async adjustWidthsAfterRender() {
      await this.$nextTick(); // wait for the next DOM update cycle
      this.adjustWidths(this.width, this.$route.name);
    },
    canLoadPage(foldersArray, folderNameQuery) {
      const currentFolder = foldersArray.find((folder) => folder.name === folderNameQuery);

      return !currentFolder?.isEmptyFolder;
    },
    async loadPageInfo() {
      await this.$store.dispatch('jobs/fetchJobByIdGuest', {
        jobId: this.currentId,
        folderName: this.$route.query?.folderName || FolderNameEnum.ALL_VULNERABILITIES,
      });

      const folderNameQuery = this.$route.query?.folderName || FolderNameEnum.ALL_VULNERABILITIES;

      const foldersArray = this.folders;

      const canLoadPage = this.canLoadPage(foldersArray, folderNameQuery);

      if (!canLoadPage) {
        this.$store.commit('jobs/setVulnerabilityPage', {
          count: 0,
          page: 0,
          vulnerabilities: [],
        });

        return;
      }

      const queryPage = Number(this.$route.query?.page);

      await this.sendPageRequest(isNaN(queryPage) ? null : queryPage);

      this.scrollToVulnerability();
    },
    scrollToVulnerability() {
      if (!this.$route.params.vulnerabilityId) return;

      setTimeout(() => {
        const el = document.getElementById(this.$route.params.vulnerabilityId);

        el && el.scrollIntoView({ behavior: 'smooth' });
      }, 1200);
    },
    toVulnerabilityScroll(scrollType, vulnerbilityId) {
      // Here we should scroll to vulnerability by id
      if (scrollType === 'QUERY_SCROLL') {
        setTimeout(() => {
          document.getElementById(vulnerbilityId).scrollIntoView({ behavior: 'smooth' });
          this.vId = null;
          this.$store.commit('jobs/clearSelectedCategory'); // clear selected category after scroll
        }, 0);
        // Here we should scroll to vulnerability by categoryType
      } else if (scrollType === 'SELECTED_SCROLL') {
        setTimeout(() => {
          let vulnerability = this.vulnerabilityPage.vulnerabilities.find(
            (vulnerability) =>
              vulnerability['vulnerability-category.descriptionTitle'] === this.selectedCategory
          );
          if (vulnerability) {
            document.getElementById(vulnerability.id).scrollIntoView({ behavior: 'smooth' });
            this.$store.commit('jobs/clearSelectedCategory'); // clear selected category after scroll
          }
        }, 500);
      }
    },
    toggleFolderSettingsPopUp(value) {
      this.isFolderSettingsPopUp = value;
    },
    deleteNotEmptyFolder(deletedFolder) {
      this.foldersForDeletingNotEmpty = this.folders.filter(
        (folder) =>
          folder.name !== deletedFolder.name && folder.name !== FolderNameEnum.ALL_VULNERABILITIES
      );

      this.isFolderSettingsPopUp = false;
      this.$refs.folderDeletePopUp.deletedFolder = deletedFolder;
      this.$refs.folderDeletePopUp.isShowModal = true;
    },
    //open popup to confirm deletion folder
    openConfirmationPopup(index, folder) {
      this.processingFolder = folder;
      this.processingFolderIndex = index;
      this.folderDeletionAccepted = false;
      this.isFolderSettingsPopUp = false;
      this.$refs.confirmationPopUp.isShowModal = true;
    },
    // confirmed deletion and start spinner
    isConfirmedDeleteFolder(index, folder) {
      this.folderDeletionAccepted = true;
      this.isFolderSettingsPopUp = true;
      this.$refs.folderSettingsPopUp.showSpinner = true;
    },
    //open edit folder popup
    openEditFolderPopUp(index, folder) {
      this.processingFolderIndex = index;
      this.processingFolder = folder;
      this.editedFolderName = '';
      this.isFolderSettingsPopUp = false;
      this.$refs.editFolderNamePopUp.showModal();
    },
    //confirmed edit
    onConfirmEditFolder(editedFolderName) {
      this.processingFolderIndex = null;
      this.editedFolderName = editedFolderName;
      this.isFolderSettingsPopUp = true;
      this.$refs.folderSettingsPopUp.showSpinner = true;
    },
    onDeleteFolderHidden() {
      this.isFolderSettingsPopUp = true;
    },
    onDeleteNotEmptyFolder({ selectedFolder, deletedFolder }) {
      this.$refs.folderSettingsPopUp.moveAndDeleteFolder(selectedFolder, deletedFolder);
    },
    async sendPageRequest(page) {
      return await this.$store.dispatch('jobs/findAndLoadGuestVulnerabilityPage', {
        id: this.currentId,
        page: page,
        limit: 20,
        shared: this.currentJob?.data.shared,
        vulnerabilityId: this.$route.params.vulnerabilityId,
        branchName: this.branchName,
        folderName: this.$route.query?.folderName || FolderNameEnum.ALL_VULNERABILITIES,
        reqType: page ? 'GET_PAGE' : 'FIND_PAGE_BY_VULNERABILITY_ID',
      });
    },
    reportTime(time) {
      return reportTime(time);
    },
    handleScroll() {
      this.toggleDisplayBtnToTop = window.scrollY > 500;
      if (this.toggleDisplayBtnToTop) {
        let btnToTop = document.querySelector('.btn-to-top');
        if (btnToTop) btnToTop.style.right = this.isSidebarVisible ? '380px' : '24px';
      }
    },
    resizeHandler() {
      this.width = window.innerWidth;
    },
    changeDownloadingReportStatus(status) {
      this.refreshLoading = status;
    },
    toTop() {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    loadSpinner(value) {
      this.refreshLoading = value;
    },
    loadRefreshSpinner(isLoadSpinner) {
      this.refreshLoading = isLoadSpinner && !this.isVulnerabilityPageLoading;
    },
    UnlockTrialNotification(type) {
      if (type === 'success') this.successNotification('reportUnlocked');
      else if (type === 'fail') this.failNotification();
    },
    onSidebarHidden() {
      this.$store.commit('filters/setSidebarVisiblity', false);
    },
    onSidebarShown() {
      this.$store.commit('filters/setSidebarVisiblity', true);
    },
    adjustWidths,
    successNotification,
    failNotification,
    calculateTotalStatisticsCount,
    isEmpty,
  },
};
</script>
<style lang="scss" scoped>
.contact-us-btn {
  position: fixed;
  bottom: 24px;
  left: 24px;
  background: orange;
  font-size: 16px;
  line-height: 1;
  padding: 16px 20px;
  z-index: 1;
  border-radius: 24px;
  font-weight: 500;
  color: #000;
  box-shadow: 0 0 12px 3px rgba(0, 0, 0, 0.15);
}

.main-report-container {
  display: flex;
  justify-content: center;
}

.sidebar {
  padding-right: 264px;
}

.page-content {
  justify-content: center;
  background: #fff;
}

.spinner__vp-wrapper {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  z-index: 3000;
  background-color: rgba(255, 255, 255, 0.7);
}

.spinner__wp {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner__my-scans-wrapper {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.7);
}

.spinner__my-scans {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.report__title {
  margin: 90px 0 27px 20px;
  font-family: Cabin, serif;
  font-weight: bold;
  font-size: 24px;
  color: #242424;
}

.component-for-small-permissions {
  display: none;
}

.main {
  display: flex;
  flex-direction: column;

  &__no-vulnerabilities-wrapper {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__no-vulnerabilities {
    font-family: Cabin, serif;
    font-style: normal;
    font-weight: bold;
    font-size: 24px;
    line-height: 150%;
    color: #242424;
  }
}

.vulnerability-statistics-title {
  margin-left: 16px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.report-time {
  font-size: 18px;
}

.statistics {
  font-size: 24px;
}

.btn-to-top {
  position: fixed;
  bottom: 50px;
  border: 4px solid transparent;
  right: 24px;
  border-radius: 50%;
  transition: 0.3s;
  z-index: 9;
}

.btn-to-top:hover {
  cursor: pointer;
  border-radius: 50%;
  border: 4px solid #e3e6ed;
}

.report-page {
  width: calc(100vw - 232px);
}

@media (max-width: 900px) {
  .report-page {
    padding-right: 0px;
  }
}

@media (max-width: 576px) {
  .report-page {
    width: 92vw;
  }

  .main-report-container {
    max-width: 100%;
  }
}
</style>
